import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { FddApi } from '../../../ui/services/server-fdd';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FiDownload, FiEdit, FiEye, FiTrash2 } from 'react-icons/fi';
import { useNotification } from '../../../ui/context/notification/NotificationContext';

export const TableDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      try {
        // El userId se obtiene del token JWT en el backend
        console.log('[TableDocuments] Fetching documents for authenticated user');
        const response = await FddApi.getDocuments();
        
        console.log('[TableDocuments] Response:', response);
        
        if (!response.hasError) {
          console.log('[TableDocuments] Documents loaded:', response.body);
          setDocuments(response.body || []);
        } else {
          console.error('[TableDocuments] Error fetching documents:', response.message);
          setDocuments([]);
        }
      } catch (error) {
        console.error('[TableDocuments] Error fetching documents:', error);
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const handleView = async (draftId) => {
    try {
      const result = await FddApi.getDocument(draftId, 'draft');
      if (result.hasError) throw new Error(result.message);
      const blob = new Blob([result.body], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error('[TableDocuments] Error al visualizar:', error);
      showNotification('No se pudo abrir el documento. Intenta de nuevo.', 'error');
    }
  };

  const handleEdit = (draftId) => {
    navigate(`/draft/${draftId}`);
  };

  const handleDeleteRequest = (draftId, title) => {
    setDeleteTarget({ id: draftId, title });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    setDeleteTarget(null);
    const result = await FddApi.deleteDraft(id);
    if (result.hasError) {
      console.error('[TableDocuments] Error al eliminar:', result.message);
      showNotification('No se pudo eliminar el documento. Intenta de nuevo.', 'error');
    } else {
      setDocuments(prev => prev.filter(d => d.pk_id_draft !== id));
      showNotification('Documento eliminado correctamente.', 'success');
    }
  };

  const handleDownload = async (draftId, filename) => {
    try {
      const result = await FddApi.downloadDraft(draftId);
      if (result.hasError) throw new Error(result.message);
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename || filename || `documento-${draftId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      console.error('Error downloading file:', error);
      showNotification('No se pudo descargar el archivo. Intenta de nuevo.', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completado':
        return 'success';
      case 'enviado':
        return 'info';
      case 'borrador':
        return 'default';
      case 'cancelado':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <div>
      {!loading && documents.length === 0 && (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: '#666',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>No hay documentos aún</p>
          <p style={{ fontSize: '14px' }}>Sube tu primer PDF para comenzar</p>
        </div>
      )}
      <DataGrid
          autoHeight
          rowSpacingType="border"
          loading={loading}
          id="dataGridDocuments"
          rows={(documents || []).map(doc => ({
            id: doc.pk_id_draft,
            title: doc.title || 'Sin título',
            created_at: doc.date_created ? new Date(doc.date_created).toLocaleDateString('es-ES') : '-',
            status: doc.status,
            signers: `${doc.signed_count || 0}/${doc.total_signers || 0}`,
            filename: doc.cloud_filename,
          }))}
          columns={columns(handleView, handleEdit, handleDownload, getStatusColor, handleDeleteRequest)}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[5, 10, 25]}
          sx={{
            '& .MuiDataGrid-cell': {
              padding: '8px',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold',
            },
          }}
      />

      {/* Diálogo de confirmación de borrado */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Eliminar documento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar <strong>{deleteTarget?.title}</strong>? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
};

const columns = (handleView, handleEdit, handleDownload, getStatusColor, handleDelete) => [
  { 
    field: "id", 
    headerName: "ID", 
    width: 80,
    align: 'center',
    headerAlign: 'center', 
  },
  {
    field: "title",
    headerName: "Título",
    flex: 1,
    minWidth: 200,
  },
  {
    field: "created_at",
    headerName: "Fecha de Creación",
    width: 150,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: "status",
    headerName: "Estado",
    width: 130,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Chip 
        label={params.value || 'Desconocido'} 
        color={getStatusColor(params.value)}
        size="small"
      />
    ),
  },
  {
    field: "signers",
    headerName: "Firmantes",
    width: 110,
    align: 'center',
    headerAlign: 'center',
    description: "Firmados / Total",
  },
  {
    field: "actions",
    headerName: "Acciones",
    width: 180,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params) => (
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
        <Tooltip title="Ver">
          <IconButton 
            size="small" 
            color="primary"
            onClick={() => handleView(params.row.id, params.row.filename)}
          >
            <FiEye />
          </IconButton>
        </Tooltip>
        <Tooltip title={['completado', 'enviado'].includes(params.row.status?.toLowerCase()) ? 'No se puede editar' : 'Editar'}>
          <span>
            <IconButton 
              size="small" 
              color="secondary"
              onClick={() => handleEdit(params.row.id)}
              disabled={['completado', 'enviado'].includes(params.row.status?.toLowerCase())}
            >
              <FiEdit />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Descargar">
          <IconButton 
            size="small" 
            color="success"
            onClick={() => handleDownload(params.row.id, params.row.filename)}
            disabled={!params.row.filename}
          >
            <FiDownload />
          </IconButton>
        </Tooltip>
        {params.row.status?.toLowerCase() === 'borrador' && (
          <Tooltip title="Eliminar borrador">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.id, params.row.title)}
            >
              <FiTrash2 />
            </IconButton>
          </Tooltip>
        )}
      </div>
    ),
  },
];
